import { useEffect, useMemo } from 'react'

import { snakeToCamel } from '@utils/format.utils'

import { NameDto } from '@model/NameDto'
import { PaymentProvider } from '@model/PaymentProviderDto'

import {
  FormFieldComponentType,
  FormFieldSelect,
  isFormFieldSelect,
} from '@global/formField/formFieldsFactory/FormFieldComponentType'

interface UseFundingCurrencySelectorParams {
  paymentProvider?: PaymentProvider
  fields: Record<string, string>
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void
  hideSinglePaymentCurrency?: boolean
}

interface UseFundingCurrencySelectorResult {
  options: NameDto<string>[]
  selectedCurrencyId?: string
  fieldName?: string
  label: string
  required: boolean
  isVisible: boolean
}

const normalizeFieldName = (fieldName: string) => fieldName.replace(/^fields\./, '')
const FUNDING_CURRENCY_FIELD_NAMES = [
  'payment_currency_id',
  'digital_currency',
  'digital_currency_id',
  'crypto_currency',
  'crypto_currency_id',
  // Some crypto channels (e.g. CHANNEL A) return "Digital Currency" selector under this BE key.
  'bank_code',
]

const isFundingCurrencyFieldName = (fieldName: string): boolean => {
  const normalizedFieldName = normalizeFieldName(fieldName)

  return FUNDING_CURRENCY_FIELD_NAMES.some((supportedFieldName) => {
    const supportedCamel = snakeToCamel(supportedFieldName)
    return (
      normalizedFieldName === supportedFieldName ||
      normalizedFieldName === supportedCamel ||
      normalizedFieldName.endsWith(`_${supportedFieldName}`) ||
      normalizedFieldName.endsWith(supportedCamel)
    )
  })
}

export const isPaymentCurrencyFormField = (
  field: Pick<FormFieldComponentType, 'name'>
): boolean => {
  const normalizedFieldName = normalizeFieldName(field.name)

  return (
    normalizedFieldName === 'payment_currency_id' ||
    normalizedFieldName === 'paymentCurrencyId' ||
    normalizedFieldName.endsWith('_payment_currency_id') ||
    normalizedFieldName.endsWith('PaymentCurrencyId')
  )
}

export const isFundingCurrencyFormField = (
  field: FormFieldComponentType
): field is FormFieldSelect => {
  return isFormFieldSelect(field) && isFundingCurrencyFieldName(field.name)
}

export const getFundingCurrencySelectField = (
  fields: FormFieldComponentType[]
): FormFieldSelect | undefined => {
  return fields.find(isFundingCurrencyFormField)
}

export const clearFundingCurrencySelection = (
  fields: Record<string, string>,
  paymentProvider: PaymentProvider
): Record<string, string> => {
  const currencySelectField = getFundingCurrencySelectField(paymentProvider.parameters.fields)
  if (!currencySelectField) return fields

  const fieldKey = snakeToCamel(currencySelectField.name).replace(/^fields\./, '')

  return { ...fields, [fieldKey]: '' }
}

// TODO: backend by some reason provide "client currency field"
export const isVisibleFundingFormField = (
  field: FormFieldComponentType,
  currencyFieldName?: string
): boolean => {
  if (isPaymentCurrencyFormField(field)) {
    return false
  }

  if (field.name === currencyFieldName) {
    return false
  }

  return true
}

export const useFundingCurrencySelector = (
  params: UseFundingCurrencySelectorParams
): UseFundingCurrencySelectorResult => {
  const { paymentProvider, fields, setFieldValue, hideSinglePaymentCurrency = false } = params

  const currencySelectField = useMemo(() => {
    if (!paymentProvider) {
      return undefined
    }

    return getFundingCurrencySelectField(paymentProvider.parameters.fields)
  }, [paymentProvider])

  const fieldPath = currencySelectField ? snakeToCamel(currencySelectField.name) : undefined
  const fieldKey = fieldPath?.replace(/^fields\./, '')

  const options: NameDto<string>[] =
    currencySelectField?.value.map((option) => ({ id: option.value, name: option.name })) ?? []

  const shouldHideSinglePaymentCurrency =
    hideSinglePaymentCurrency &&
    !!currencySelectField &&
    isPaymentCurrencyFormField(currencySelectField) &&
    options.length === 1
  const singlePaymentCurrencyId = shouldHideSinglePaymentCurrency ? options[0].id : undefined

  const currentFieldValue = fieldKey ? fields[fieldKey] : undefined
  const selectedCurrencyId = currentFieldValue || singlePaymentCurrencyId

  useEffect(() => {
    if (!fieldPath || !singlePaymentCurrencyId || currentFieldValue) {
      return
    }

    setFieldValue(fieldPath, singlePaymentCurrencyId)
  }, [currentFieldValue, fieldPath, setFieldValue, singlePaymentCurrencyId])

  useEffect(() => {
    if (
      !fieldPath ||
      !selectedCurrencyId ||
      !paymentProvider ||
      !isPaymentCurrencyFormField({ name: fieldPath })
    ) {
      return
    }

    const newCurrency = paymentProvider.parameters.currencies.find(
      (currency) => currency.id === selectedCurrencyId
    )
    if (newCurrency && paymentProvider.currency?.id !== newCurrency.id) {
      setFieldValue('paymentProvider.currency', {
        ...paymentProvider.currency,
        id: newCurrency.id,
        name: newCurrency.name,
      })
    }
  }, [fieldPath, paymentProvider, selectedCurrencyId, setFieldValue])

  return {
    options,
    selectedCurrencyId,
    fieldName: fieldPath,
    label: currencySelectField?.label ?? '',
    required: currencySelectField?.required ?? false,
    isVisible: options.length > 0 && !shouldHideSinglePaymentCurrency,
  }
}
